import { h } from "../core/dom.js";
import { icon } from "../lib/icons.js";

// ── Toast ────────────────────────────────────────────────────────────────────
export function toast(message, type = "info") {
  const root = document.getElementById("toast-root");
  const el = h("div", { class: ["toast", `toast-${type}`], role: type === "error" ? "alert" : "status", "aria-atomic": "true" }, message);
  root.appendChild(el);
  requestAnimationFrame(() => el.classList.add("toast-show"));
  setTimeout(() => {
    el.classList.remove("toast-show");
    setTimeout(() => el.remove(), 300);
  }, type === "error" ? 4000 : 2600);
}

// ── Modal ─────────────────────────────────────────────────────────────────────
const modalStack = [];
let modalSequence = 0;
let appWasInert = false;
const focusable = (panel) => [...panel.querySelectorAll('button, a[href], input, select, textarea, [tabindex="0"], summary')]
  .filter(el => !el.disabled && !el.closest('[inert]') && el.getClientRects().length);

export function openModal({ title, content, className = "", fullscreen = false, onClose, canClose } = {}) {
  const root = document.getElementById("modal-root");
  const app = document.getElementById("app");
  const trigger = document.activeElement;
  const headingId = `dialog-heading-${++modalSequence}`;
  let closed = false;
  const syncStack = () => {
    modalStack.forEach(entry => { entry.overlay.inert = entry !== modalStack.at(-1); });
    if (app) app.inert = modalStack.length ? true : appWasInert;
  };
  const close = () => {
    if (closed || canClose?.() === false) return;
    closed = true;
    const wasTop = modalStack.at(-1) === entry;
    modalStack.splice(modalStack.indexOf(entry), 1);
    overlay.inert = true;
    overlay.setAttribute("aria-hidden", "true");
    overlay.style.pointerEvents = "none";
    overlay.classList.remove("modal-open");
    setTimeout(() => overlay.remove(), 200);
    document.removeEventListener("keydown", onKey);
    syncStack();
    onClose?.();
    queueMicrotask(() => {
      if (!wasTop) return;
      const top = modalStack.at(-1);
      if (top) {
        if (!top.panel.contains(document.activeElement)) (focusable(top.panel)[0] || top.panel).focus();
      } else if (trigger?.isConnected && !trigger.closest('[inert]')) trigger.focus({ preventScroll: true });
      else document.querySelector('.screen h1')?.focus({ preventScroll: true });
    });
  };
  const onKey = (e) => {
    if (modalStack.at(-1) !== entry) return;
    if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); close(); }
    if (e.key === "Tab") {
      const items = focusable(panel), first = items[0] || panel, last = items.at(-1) || panel;
      if (!panel.contains(document.activeElement) || (e.shiftKey && document.activeElement === first) || (!e.shiftKey && document.activeElement === last) || document.activeElement === panel) {
        e.preventDefault(); (e.shiftKey ? last : first).focus();
      }
    }
  };

  const panel = h(
    "div",
    { class: ["modal", fullscreen && "modal-fullscreen", className], role: "dialog", "aria-modal": "true", "aria-labelledby": headingId, tabindex: "-1" },
    h(
      "div",
      { class: "modal-head" },
      h("h3", { id: headingId, class: !title && "sr-only" }, title || content?.querySelector?.("h1,h2,h3")?.textContent || "Workout details"),
      h("button", { class: "icon-btn", onClick: close, "aria-label": "Close" }, icon("x"))
    ),
    h("div", { class: "modal-body" }, content)
  );
  const overlay = h(
    "div",
    {
      class: "modal-overlay",
      onClick: (e) => {
        if (e.target === overlay) close();
      },
    },
    panel
  );
  const entry = { overlay, panel };
  if (!modalStack.length) appWasInert = !!app?.inert;
  modalStack.push(entry);
  root.appendChild(overlay);
  syncStack();
  document.addEventListener("keydown", onKey);
  requestAnimationFrame(() => {
    if (closed) return;
    overlay.classList.add("modal-open");
    if (modalStack.at(-1) === entry && !panel.contains(document.activeElement)) (panel.querySelector('[autofocus]') || focusable(panel)[0] || panel).focus({ preventScroll: true });
  });
  return { close, panel };
}

export function confirmDialog({ title = "Are you sure?", message = "", confirmText = "Confirm", danger = false } = {}) {
  return new Promise((resolve) => {
    let decided = false;
    const finish = (val) => {
      if (decided) return;
      decided = true;
      modal.close();
      resolve(val);
    };
    const body = h(
      "div",
      { class: "confirm" },
      message && h("p", null, message),
      h(
        "div",
        { class: "confirm-actions" },
        h("button", { class: "btn btn-ghost", onClick: () => finish(false) }, "Cancel"),
        h("button", { class: ["btn", danger ? "btn-danger" : "btn-primary"], onClick: () => finish(true) }, confirmText)
      )
    );
    const modal = openModal({ title, content: body, className: "modal-confirm", onClose: () => finish(false) });
  });
}

// ── Small building blocks ──────────────────────────────────────────────────────
export function spinner(label = "Loading…") {
  return h("div", { class: "loading" }, h("div", { class: "spinner" }), h("span", null, label));
}

export function emptyState({ icon: iconName, title, message, action } = {}) {
  return h(
    "div",
    { class: "empty-state" },
    iconName && h("div", { class: "empty-icon" }, icon(iconName, { size: 40 })),
    h("h3", null, title),
    message && h("p", null, message),
    action
  );
}

export function errorState(message = "Something went wrong.", onRetry) {
  return h(
    "div",
    { class: "empty-state" },
    h("h3", null, "⚠️ Error"),
    h("p", null, message),
    onRetry && h("button", { class: "btn btn-primary", onClick: onRetry }, "Retry")
  );
}
